/* eslint-disable react-hooks/exhaustive-deps */
import { User, UserTermsAcceptanceResponse } from '@eagle/core-data-types';
import { Alert, Box } from '@ethings/ui-toolkit';
import { useQuery } from '@tanstack/react-query';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { useAuth, useAuthenticated } from '../../auth';
import { ErrorMessage, FlexBox, Icon, MiddleSpinner } from '../../components';
import { useConfig, useCustomRoutes, useFetchOneCache, usePromise, useTermsAndConditionsLabel } from '../../hooks';
import { CacheDataTypes, PageAction, SetState } from '../../types';
import { ifTranslationExists, useHasAuthorization } from '../../util';
import { NotFound } from '../errors';
import { ProfilePageController } from './profile-page-controller';
import { ProfileTabTypes, TermsAndConditionsDocument } from './profile-page-types';

const SUBSCRIPTION_ADMIN_ROLE = ['subscription-administrator'] as const;

export const ProfilePage: FC = () => {
  const { user, restClient } = useAuthenticated();
  const myUserCache = useFetchOneCache(CacheDataTypes.MY_USER);
  const [refresh, setRefresh] = useState(new Date());
  const [userData, userDataError, userDataStatus] = usePromise(() => myUserCache.one<User>(user._id), [refresh]);
  const userTermsAcceptanceQuery = useQuery({
    queryKey: ['user-terms-acceptance', user._id],
    queryFn: async () => restClient.userTermsAcceptance.listUserTermsAcceptance(user._id),
    retry: false,
    staleTime: Infinity,
  });

  if (userDataStatus === 'pending' || userTermsAcceptanceQuery.status === 'pending') {
    return (
      <FlexBox sx={{ height: '200px', justifyContent: 'center', width: '100vw' }}>
        <MiddleSpinner />
      </FlexBox>
    );
  }

  if (userDataError) {
    return (
      <Box m="1rem">
        <Alert severity="error">
          <ErrorMessage error={userDataError} />
        </Alert>
      </Box>
    );
  }

  if (!userData) {
    return (
      <FlexBox>
        <NotFound />
      </FlexBox>
    );
  }

  return (
    <ProfilePageInner
      setRefresh={setRefresh}
      userData={userData}
      userTermsAcceptance={userTermsAcceptanceQuery.data ?? []}
    />
  );
};

interface ProfilePageInnerProps {
  setRefresh: SetState<Date>;
  userData: User;
  userTermsAcceptance: UserTermsAcceptanceResponse;
}

const ProfilePageInner: FC<ProfilePageInnerProps> = ({ setRefresh, userData, userTermsAcceptance }) => {
  const location = useLocation();
  const { signOut, state } = useAuth();
  const { hasAuthorization } = useHasAuthorization(state);
  const { subscription } = useCustomRoutes();
  const navigate = useNavigate();
  const { t } = useTranslation(['common']);
  const { termsOfService, productName } = useConfig();
  const { hasOverride, termsLabel } = useTermsAndConditionsLabel();

  const getTermsAndConditionsDocuments = (): TermsAndConditionsDocument[] => {
    const result: TermsAndConditionsDocument[] = [];

    if (termsOfService?.documentId) {
      const fallbackTitle = hasOverride
        ? termsLabel
        : t('pages.profile.terms-and-conditions.implicit-terms-conditions.labels');
      const title = productName
        ? ifTranslationExists(
          `common:pages.profile.terms-and-conditions.${productName.replace(/[.\s]/g, '_')}.implicit-terms-conditions.labels`,
          fallbackTitle,
        )
        : fallbackTitle;

      const origin = window.location.origin;
      const domain = window.location.host;
      const url = `${origin}/web-asset/${termsOfService.documentId}?domain=${domain}`;

      result.push({
        type: 'domain-config',
        title,
        url,
      });
    }

    for (const document of userTermsAcceptance) {
      result.push({
        type: 'api',
        documentId: document.documentId,
        versionId: document.versionId,
        language: document.language,
        title: document.display,
        acceptedOn: document.created.when,
      });
    }

    return result;
  };

  const termsAndConditionsDocuments = getTermsAndConditionsDocuments();

  const actions: PageAction[] = [
    {
      icon: <Icon icon="MailIcon" />,
      label: t('common:page.profile.notification-settings.labels'),
      onClick: () => navigate('/profile'),
    },
    ...(hasAuthorization(SUBSCRIPTION_ADMIN_ROLE)
      ? [
        {
          icon: <Icon icon="AlertIcon" />,
          label: t('common:page.profile.my-subscriptions.labels'),
          onClick: () => navigate(`/profile/${subscription}`),
        },
      ]
      : []
    ),
    ...(termsAndConditionsDocuments.length > 0
      ? [
        {
          icon: <Icon icon="FileText" />,
          label: termsLabel,
          onClick: () => navigate('/profile/terms-and-conditions'),
        },
      ]
      : []
    ),
    {
      icon: <Icon icon="SignOutIcon" />,
      label: t('common:component.auth.labels.sign-out'),
      onClick: () => signOut(),
    },
  ];

  const getCurrentTab = (): ProfileTabTypes => {
    if (location.pathname.includes('subscription')) {
      return ProfileTabTypes.SUBSCRIPTIONS;
    }
    if (location.pathname.includes('terms-and-conditions')) {
      return ProfileTabTypes.TERMS_AND_CONDITIONS;
    }
    return ProfileTabTypes.NOTIFICATIONS;
  };

  return (
    <ProfilePageController
      actions={actions}
      setRefresh={setRefresh}
      tab={getCurrentTab()}
      termsAndConditionsDocuments={termsAndConditionsDocuments}
      userData={userData}
    />
  );
};
